import { useState } from "react";
import { getAiSuggestions } from "../../actions/aiAction";
import "./AiSuggestions.css";

const PROFILES = [
  {
    id: "challenger",
    name: "The Challenger",
    icon: "bx-shield-quarter",
    tagline: "Am I being honest with myself?",
    desc: "Plays devil's advocate. Surfaces blind spots and pressure-tests your assumptions.",
    canCommit: false,
    loadingText: "Sharpening the hard questions...",
    color: "#7c3aed",
  },
  {
    id: "mirror",
    name: "The Mirror",
    icon: "bx-glasses",
    tagline: "What patterns am I not seeing?",
    desc: "Reflects your journey back at you. No action items, just honest patterns.",
    canCommit: false,
    loadingText: "Reading between the lines...",
    color: "#0891b2",
  },
  {
    id: "therapist",
    name: "The Therapist",
    icon: "bx-heart",
    tagline: "Am I being kind enough to myself?",
    desc: "Reframes your struggles, highlights what you're overlooking, and validates what's hard.",
    canCommit: false,
    loadingText: "Reading your journey with care...",
    color: "#be185d",
  },
];

/**
 * Dmyor Assist overlay with profile picker.
 *
 * Props:
 *  - nodeId:      Post._id
 *  - onClose:     () => void
 *  - onApplied:   () => void
 */
const AiSuggestions = ({ nodeId, onClose }) => {
  // screen: "pick" | "loading" | "result" | "error"
  const [screen, setScreen] = useState("pick");
  const [activeProfile, setActiveProfile] = useState(null);
  const [result, setResult] = useState(null);

  const [fromCache, setFromCache] = useState(false);

  const [error, setError] = useState(null);

  const runProfile = async (profileId, force = false) => {
    localStorage.setItem('dmyor_gs_assist', '1');
    const profile = PROFILES.find((p) => p.id === profileId);
    setActiveProfile(profile);
    setScreen("loading");
    setResult(null);
    setFromCache(false);
    setError(null);

    try {
      const data = await getAiSuggestions(nodeId, profileId, force);
      setResult(data);
      setFromCache(!!data.fromCache);
      setScreen("result");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to generate suggestions. Please try again."
      );
      setScreen("error");
    }
  };

  const handleBack = () => {
    setScreen("pick");
    setActiveProfile(null);
    setResult(null);
    setError(null);
  };

  const isOnSubScreen = screen !== "pick";

  const CacheBar = () => fromCache ? (
    <div className="ai-cache-bar">
      <span className="ai-cache-label">
        <i className="bx bx-time-five" /> Showing last saved response — no new updates since
      </span>
      <button
        className="ai-cache-regen"
        onClick={() => runProfile(activeProfile.id, true)}
      >
        Regenerate
      </button>
    </div>
  ) : null;

  return (
    <div className="overlay" onClick={onClose}>
      <div className="ai-suggest-panel" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="ai-suggest-header">
          <span className="ai-suggest-title">
            {isOnSubScreen && activeProfile ? (
              <>
                <button className="ai-suggest-back" onClick={handleBack} title="Back">
                  <i className="bx bx-arrow-back" />
                </button>
                <i
                  className={`bx ${activeProfile.icon}`}
                  style={{ color: activeProfile.color }}
                />
                {activeProfile.name}
              </>
            ) : (
              <>
                <i className="bx bx-bulb" />
                Dmyor Assist
              </>
            )}
          </span>
          <button className="ai-suggest-close" onClick={onClose}>&times;</button>
        </div>

        {/* Profile Picker */}
        {screen === "pick" && (
          <div className="ai-profile-picker">
            <p className="ai-profile-intro">
              Choose how you want to engage with your node today.
            </p>
            <div className="ai-profile-list">
              {PROFILES.map((p) => (
                <button
                  key={p.id}
                  className="ai-profile-card"
                  style={{ "--profile-color": p.color }}
                  onClick={() => runProfile(p.id)}
                >
                  <div className="ai-profile-card-top">
                    <span className="ai-profile-icon-wrap">
                      <i className={`bx ${p.icon}`} style={{ color: p.color }} />
                    </span>
                    <div className="ai-profile-meta">
                      <span className="ai-profile-name">{p.name}</span>
                      <span className="ai-profile-tagline">{p.tagline}</span>
                    </div>
                    {p.canCommit && (
                      <span className="ai-profile-commit-badge">Can Commit</span>
                    )}
                  </div>
                  <p className="ai-profile-desc">{p.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading */}
        {screen === "loading" && (
          <div className="ai-suggest-loading">
            <div
              className="ai-suggest-bar-track"
              style={{ "--profile-color": activeProfile?.color || "#f17501" }}
            >
              <div className="ai-suggest-bar-fill" />
            </div>
            <span className="ai-suggest-loading-text">
              {activeProfile?.loadingText || "Thinking..."}
            </span>
          </div>
        )}

        {/* Error */}
        {screen === "error" && (
          <div className="ai-suggest-error">
            <span className="ai-suggest-error-text">{error}</span>
            <div className="ai-suggest-actions">
              <button className="ai-suggest-btn ai-suggest-btn-retry" onClick={handleBack}>
                Back
              </button>
              <button
                className="ai-suggest-btn ai-suggest-btn-apply"
                onClick={() => activeProfile && runProfile(activeProfile.id)}
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Result: Challenger */}
        {screen === "result" && activeProfile?.id === "challenger" && result && (
          <>
            <CacheBar />
            {result.opening && (
              <div className="ai-suggest-opening" style={{ "--profile-color": "#7c3aed" }}>
                <p>{result.opening}</p>
              </div>
            )}
            {(result.challenges || []).length > 0 ? (
              <div className="ai-insight-list">
                {result.challenges.map((item, i) => (
                  <div key={i} className="ai-insight-item">
                    <span className="ai-insight-marker" style={{ color: "#7c3aed" }}>?</span>
                    <span className="ai-insight-text">{item}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="ai-insight-empty">Nothing to challenge here — the thinking looks solid.</p>
            )}
            <div className="ai-suggest-actions">
              <button className="ai-suggest-btn ai-suggest-btn-retry" onClick={handleBack}>
                Back
              </button>
            </div>
          </>
        )}

        {/* Result: Mirror */}
        {screen === "result" && activeProfile?.id === "mirror" && result && (
          <>
            <CacheBar />
            {result.opening && (
              <div className="ai-suggest-opening" style={{ "--profile-color": "#0891b2" }}>
                <p>{result.opening}</p>
              </div>
            )}
            {(result.reflections || []).length > 0 ? (
              <div className="ai-insight-list">
                {result.reflections.map((item, i) => (
                  <div key={i} className="ai-insight-item">
                    <span className="ai-insight-marker" style={{ color: "#0891b2" }}>◎</span>
                    <span className="ai-insight-text">{item}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="ai-insight-empty">Not enough entries yet to see clear patterns. Keep documenting.</p>
            )}
            <div className="ai-suggest-actions">
              <button className="ai-suggest-btn ai-suggest-btn-retry" onClick={handleBack}>
                Back
              </button>
            </div>
          </>
        )}

        {/* Result: Therapist */}
        {screen === "result" && activeProfile?.id === "therapist" && result && (
          <>
            <CacheBar />
            {result.opening && (
              <div className="ai-suggest-opening" style={{ "--profile-color": "#be185d" }}>
                <p>{result.opening}</p>
              </div>
            )}
            {(result.reflections || []).length > 0 ? (
              <div className="ai-insight-list">
                {result.reflections.map((item, i) => (
                  <div key={i} className="ai-insight-item">
                    <span className="ai-insight-marker" style={{ color: "#be185d" }}>♡</span>
                    <span className="ai-insight-text">{item}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="ai-insight-empty">Not enough to go on yet. Add a few more updates and come back.</p>
            )}
            <div className="ai-suggest-actions">
              <button className="ai-suggest-btn ai-suggest-btn-retry" onClick={handleBack}>
                Back
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default AiSuggestions;
